/** Tab „Server-Profile": je Server Spitzname, Server-Avatar/-Banner und Server-Bio. */
import { FIXED_LIMITS } from '@lagerfeuer/shared';
import { useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router';
import { errorMessage } from '../../lib/api';
import { membersApi } from '../../lib/endpoints';
import { useAppStore } from '../../store/app';
import { can, useServerPermissions } from '../../store/selectors';
import { toast } from '../../store/ui';
import { ProfileCard } from '../profile/ProfileCard';
import { ImageUpload } from './ImageUpload';
import { applyMyMember } from './useSettings';

export function ServerProfileTab() {
  const servers = useAppStore((s) => s.servers);
  const order = useAppStore((s) => s.serverOrder);
  const ids = [...order.filter((id) => servers[id]), ...Object.keys(servers).filter((id) => !order.includes(id))];
  const [params] = useSearchParams();
  // `?server=<id>` (z. B. aus dem Servermenü „Serverprofil bearbeiten") wählt den Server vor.
  const [serverId, setServerId] = useState<string>(params.get('server') ?? ids[0] ?? '');
  const active = servers[serverId] ? serverId : ids[0];
  if (!active) return <p className="empty">Du bist noch in keinem Server.</p>;
  return (
    <div className="stack">
      <div className="field">
        <label htmlFor="sp-server">Server</label>
        <select id="sp-server" className="select" value={active} onChange={(e) => setServerId(e.target.value)} data-testid="server-profile-select">
          {ids.map((id) => (
            <option key={id} value={id}>
              {servers[id]!.name}
            </option>
          ))}
        </select>
      </div>
      <ServerProfileEditor key={active} serverId={active} />
    </div>
  );
}

function ServerProfileEditor({ serverId }: { serverId: string }) {
  const server = useAppStore((s) => s.servers[serverId])!;
  const me = useAppStore((s) => s.me)!;
  const maxBio = useAppStore((s) => s.limits.maxBioLength);
  const perms = useServerPermissions(serverId);
  const canNick = can(perms, 'CHANGE_NICKNAME');
  const member = server.me;
  const [nickname, setNickname] = useState(member.nickname ?? '');
  const [bio, setBio] = useState(member.serverBio ?? '');
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [bannerPreview, setBannerPreview] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dirty = nickname !== (member.nickname ?? '') || bio !== (member.serverBio ?? '');
  const bioTooLong = maxBio > 0 && bio.length > maxBio;

  const save = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const body: { nickname?: string | null; serverBio?: string | null } = {};
      if (nickname !== (member.nickname ?? '')) body.nickname = nickname.trim() || null;
      if (bio !== (member.serverBio ?? '')) body.serverBio = bio.trim() || null;
      applyMyMember(await membersApi.updateMe(serverId, body));
      toast.success('Server-Profil gespeichert');
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="lf-profile-editor">
      <form className="stack lf-profile-form" onSubmit={save} data-testid="server-profile-form">
        <div className="field">
          <label htmlFor="sp-nick">Spitzname</label>
          <input
            id="sp-nick"
            className="input"
            value={nickname}
            onChange={(e) => setNickname(e.target.value)}
            maxLength={FIXED_LIMITS.nicknameMax}
            placeholder={me.displayName ?? me.username}
            disabled={!canNick}
            data-testid="server-profile-nickname"
          />
          {!canNick && <span className="field-hint">Dir fehlt in diesem Server das Recht, deinen Spitznamen zu ändern.</span>}
        </div>
        <ImageUpload
          label="Server-Avatar"
          kind="avatar"
          currentUrl={member.serverAvatarUrl}
          onPreview={setAvatarPreview}
          onUpload={async (f) => applyMyMember(await membersApi.setMyAvatar(serverId, f))}
          onRemove={async () => applyMyMember(await membersApi.removeMyAvatar(serverId))}
          testId="server-avatar-upload"
        />
        <ImageUpload
          label="Server-Banner"
          kind="banner"
          currentUrl={member.serverBannerUrl}
          onPreview={setBannerPreview}
          onUpload={async (f) => applyMyMember(await membersApi.setMyBanner(serverId, f))}
          onRemove={async () => applyMyMember(await membersApi.removeMyBanner(serverId))}
          testId="server-banner-upload"
        />
        <div className="field">
          <label htmlFor="sp-bio">Server-Bio</label>
          <textarea id="sp-bio" className="textarea" rows={4} value={bio} onChange={(e) => setBio(e.target.value)} aria-invalid={bioTooLong} data-testid="server-profile-bio" />
          <span className={bioTooLong ? 'error-text' : 'field-hint'}>
            {bio.length}
            {maxBio > 0 ? ` / ${maxBio}` : ''} Zeichen
          </span>
        </div>
        {error && (
          <p className="error-text" role="alert" data-testid="server-profile-error">
            {error}
          </p>
        )}
        <div className="row">
          <span className="spacer" />
          <button
            type="button"
            className="btn btn-ghost"
            disabled={!dirty || busy}
            onClick={() => {
              setNickname(member.nickname ?? '');
              setBio(member.serverBio ?? '');
            }}
          >
            Zurücksetzen
          </button>
          <button type="submit" className="btn btn-primary" disabled={!dirty || busy || bioTooLong} data-testid="server-profile-save">
            Speichern
          </button>
        </div>
      </form>
      <aside className="lf-profile-preview" aria-label="Vorschau">
        <div className="label">Vorschau in {server.name}</div>
        <ProfileCard
          user={me}
          nickname={nickname || null}
          avatarUrl={avatarPreview ?? member.serverAvatarUrl}
          bannerUrl={bannerPreview ?? member.serverBannerUrl}
          bio={bio || null}
          status={me.status}
          customStatus={me.customStatus}
          serverId={serverId}
          testId="server-profile-preview"
        />
      </aside>
    </div>
  );
}
